import { Formik, FormikHelpers } from 'formik';
import React, { useMemo, useState } from 'react';
import { UserPayloadInput } from 'interface/createUpdateForm';
import { Role } from 'interface/role';
import { Skill } from 'interface/skill';
import { EmployeeLike } from 'interface/userUtils';

import Stepper from '../../stepper';
import ImportSheet from '../../importSheet';
import { NEXT, PREV, COMPLETE, ERROR, getStepperNavigationList } from 'constants/createEmployeeForm';

// Components
import { error } from 'utils/toast';
import { handleError } from 'utils/errorHandler';
import { getDuplicateErrorLabel } from 'utils/string';
import { getFullName, isUpComingStatusTerminated } from 'utils/user';

import * as userService from 'services/user';
import FormWrapperHeader from './FormWrapperHeader';
import FormWrapperBottom from './FormWrapperBottom';
import AddDesignationModal from '../leapfrogHistory/components/AddDesignationModal';
import AddEngagementStatusModal from '../leapfrogHistory/components/AddEngagementStatusModal';

import { EMPLOYEE_STATUSES } from 'constants/employeeStatus';
import { ROLES_IDS, USER_FETCH_FIELDS } from 'constants/appConstants';
import useEmployeeDebounceOptions from 'hooks/useEmployeeDebounceOptions';

type AvailabilityInput = { value?: string; type?: string } | { hours: number; minutes: number };
type EmployeeFormValues = Omit<UserPayloadInput, 'availabilityStartTime' | 'availabilityEndTime'> & {
  availabilityStartTime?: AvailabilityInput;
  availabilityEndTime?: AvailabilityInput;
};

interface DuplicateField {
  value: 'username' | 'empId';
  label: string;
}

interface FormWrapperProps {
  initialFormData: EmployeeFormValues;
  isCreateForm: boolean;
  onSubmit: (values: EmployeeFormValues) => Promise<void> | void;
  isSubmitting: boolean;
  canEdit: (field: string) => boolean;
  designations: Array<{ id: number; name: string }>;
  employees: EmployeeLike[];
  departments: Array<{ id: number; name: string }>;
  designationAreas: Array<{ id: number; name: string }>;
  engagementStatus: Array<{ id: number; name: string }>;
  roles: Role[];
  skills: Skill[];
  empId?: number | string | null;
}

const FormWrapper: React.FC<FormWrapperProps> = (props) => {
  const {
    initialFormData,
    isCreateForm,
    onSubmit,
    isSubmitting,
    canEdit,
    designations,
    employees,
    departments,
    designationAreas,
    engagementStatus,
    roles,
    empId,
  } = props;

  const stepperNavigationList = useMemo(
    () => getStepperNavigationList(isCreateForm).map((item) => ({ ...item, state: '' })),
    [isCreateForm]
  );

  const [duplicateErrors, setDuplicateErrors] = useState<Partial<Record<'username' | 'empId', string>>>({});
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);

  const stepperNavigationSourceKey = stepperNavigationList.map((item) => item.id).join(':');
  const [stepperNavigationDraft, setStepperNavigationDraft] = useState(() => ({
    sourceKey: stepperNavigationSourceKey,
    items: isCreateForm ? stepperNavigationList : stepperNavigationList.slice(0, -1),
  }));
  const stepperNavigationItems =
    stepperNavigationDraft.sourceKey === stepperNavigationSourceKey
      ? stepperNavigationDraft.items
      : stepperNavigationList;

  const [currentForm, setCurrentForm] = useState(stepperNavigationList[0]);

  const [designationModal, setDesignationModal] = useState<{ isOpen: boolean; type?: string; editIndex?: number }>({
    isOpen: false,
    type: 'Add',
  });
  const [employeeStatusModal, setEmployeeStatusModal] = useState<{
    isOpen: boolean;
    type?: string;
    editIndex?: number;
  }>({
    isOpen: false,
    type: 'Add',
  });

  const [shouldForcefullyShowError, setShouldForcefullyShowError] = useState(false);

  const updateCurrentFormState = (formId: number, state: string) => {
    setStepperNavigationDraft((draft) => {
      const currentItems = draft.sourceKey === stepperNavigationSourceKey ? draft.items : stepperNavigationList;
      const currentItem = currentItems.find((item) => item.id === formId);

      if (!currentItem) {
        if (draft.sourceKey === stepperNavigationSourceKey) {
          return draft;
        }

        return {
          sourceKey: stepperNavigationSourceKey,
          items: currentItems,
        };
      }

      if (currentItem.state === state) {
        if (draft.sourceKey === stepperNavigationSourceKey) {
          return draft;
        }

        return {
          sourceKey: stepperNavigationSourceKey,
          items: currentItems,
        };
      }

      return {
        sourceKey: stepperNavigationSourceKey,
        items: currentItems.map((item) => {
          if (item.id !== formId) {
            return item;
          }

          return { ...item, state };
        }),
      };
    });

    setCurrentForm((form) => {
      if (form.id !== formId || form.state === state) {
        return form;
      }

      return { ...form, state };
    });
  };

  const handleNavigate = (id: number, action?: string) => {
    if (action === NEXT) {
      id += 1;
    } else if (action === PREV) {
      id -= 1;
    }

    const nextForm = stepperNavigationItems.find((item) => item.id === id);
    if (nextForm) {
      setCurrentForm(nextForm);
    }
  };

  const getDuplicateFields = async (values: EmployeeFormValues): Promise<DuplicateField[]> => {
    const { empId, username } = values;

    const duplicateEntries: DuplicateField[] = [];

    try {
      setIsCheckingDuplicates(true);
      if (username !== initialFormData.username) {
        const { data } = (await userService.fetchAll({
          email: username,
          empStatus: EMPLOYEE_STATUSES.join(','),
        })) as { data: Array<Record<string, string | number | boolean | null>> };

        if (data.length > 0) {
          duplicateEntries.push({ value: 'username', label: 'Email' });
        }
      }

      if (empId !== initialFormData.empId) {
        const { data } = (await userService.fetchAll({
          empId,
          empStatus: EMPLOYEE_STATUSES.join(','),
        })) as { data: Array<Record<string, string | number | boolean | null>> };

        if (data.length > 0) {
          duplicateEntries.push({ value: 'empId', label: 'Employee Id' });
        }
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsCheckingDuplicates(false);
    }

    return duplicateEntries;
  };

  const checkIsValid = () =>
    isCreateForm
      ? stepperNavigationItems.every(({ state }) => state === COMPLETE) &&
        Object.values(duplicateErrors).every((error) => !error)
      : stepperNavigationItems.every(({ state }) => state !== ERROR);

  const handleSubmit = async (values: EmployeeFormValues, actions: FormikHelpers<EmployeeFormValues>) => {
    const fields = await getDuplicateFields(values);

    if (fields.length > 0) {
      fields.forEach((field) => {
        actions.setFieldError(field.value, getDuplicateErrorLabel(field.label));
        setDuplicateErrors((error) => ({ ...error, [field.value]: getDuplicateErrorLabel(field.label) }));

        setCurrentForm(stepperNavigationItems[0]);

        handleError({ error: 'Error' }, { title: 'Error', message: `${field.label} already exists` });
      });

      return;
    }

    setDuplicateErrors({});

    updateCurrentFormState(currentForm.id, COMPLETE);

    actions.setTouched({});
    if (currentForm.id !== stepperNavigationItems.length) {
      handleNavigate(currentForm.id, NEXT);

      return;
    }

    const shouldBeRedirectedForm = stepperNavigationItems.find(({ formSchema }) => {
      return !formSchema.isValidSync(values);
    });

    if (shouldBeRedirectedForm) {
      updateCurrentFormState(shouldBeRedirectedForm.id, ERROR);
      setCurrentForm({ ...shouldBeRedirectedForm, state: ERROR });
      setShouldForcefullyShowError(true);
      actions.validateForm();

      error({ title: 'Error', message: shouldBeRedirectedForm.detail });

      return;
    }

    const toAvailabilityValue = (
      availability: AvailabilityInput | undefined
    ): { value?: string; type?: string } | undefined => {
      if (!availability) {
        return undefined;
      }

      if ('hours' in availability) {
        const hours = String(availability.hours).padStart(2, '0');
        const minutes = String(availability.minutes).padStart(2, '0');
        return { value: `${hours}:${minutes}`, type: 'HOUR_MINUTE' };
      }

      return availability;
    };

    const normalizedValues: UserPayloadInput = {
      ...values,
      availabilityStartTime: toAvailabilityValue(values.availabilityStartTime),
      availabilityEndTime: toAvailabilityValue(values.availabilityEndTime),
    };

    const employeePayload = userService.getUserPayload(normalizedValues);

    await onSubmit(employeePayload as UserPayloadInput);
  };

  const ActiveComponent = currentForm.formComponent as React.ElementType;
  const validationSchema = currentForm.formSchema;

  const [initialValues, setInitialValues] = useState<EmployeeFormValues>(initialFormData);

  const leaveIssuerDebounceOptions = useEmployeeDebounceOptions(initialValues.leaveIssuerId);

  const teamManagerDebounceOptions = useEmployeeDebounceOptions(initialValues.teamManagerId, {
    fields: USER_FETCH_FIELDS.ROLES,
    roleIds: ROLES_IDS.TEAM_MANAGER,
  });

  const appraiserDebounceOptions = useEmployeeDebounceOptions(initialValues.appraiserId);

  const coAppraiserDebounceOptions = useEmployeeDebounceOptions(initialValues.coAppraiserId);

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formikProps) => {
        const handleSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
          formikProps.handleSubmit(e);
          updateCurrentFormState(currentForm.id, ERROR);
        };

        return (
          <>
            <AddDesignationModal
              isOpen={designationModal.isOpen}
              designations={designations}
              areas={designationAreas}
              buttonLabel={designationModal.type}
              isCreateForm={isCreateForm}
              onClose={() => setDesignationModal({ isOpen: false })}
              editIndex={designationModal.editIndex}
              values={{
                joinDate: formikProps.values.joinDate,
                designationAreaHistory: formikProps.values.designationAreaHistory,
              }}
              touched={{
                designationAreaHistory:
                  (formikProps.touched.designationAreaHistory as
                    | { designation?: boolean; area?: boolean; transitionDate?: boolean }
                    | undefined) || undefined,
              }}
              handleChange={formikProps.handleChange}
              handleBlur={formikProps.handleBlur}
              setFieldTouched={formikProps.setFieldTouched}
            />
            <AddEngagementStatusModal
              isCreateForm={isCreateForm}
              isOpen={employeeStatusModal.isOpen}
              buttonLabel={employeeStatusModal.type}
              engagementStatus={engagementStatus}
              onClose={(editIndex) => setEmployeeStatusModal({ isOpen: false, editIndex })}
              editIndex={employeeStatusModal.editIndex}
              initialValues={initialValues}
              empId={empId}
              values={{
                joinDate: formikProps.values.joinDate || '',
                empStatusHistory: formikProps.values.empStatusHistory || [],
                firstName: formikProps.values.firstName,
                lastName: formikProps.values.lastName,
              }}
              touched={{
                empStatusHistory:
                  (formikProps.touched.empStatusHistory as
                    | {
                        engagementStatus?: boolean;
                        transitionDate?: boolean;
                        endDate?: boolean;
                        empStatusDetails?: Array<{ category?: boolean; date?: boolean }>;
                      }
                    | undefined) || undefined,
              }}
              handleChange={formikProps.handleChange}
              handleBlur={formikProps.handleBlur}
              setFieldTouched={formikProps.setFieldTouched}
            />

            <div className="container employee-form">
              <div className="d-flex flex-column">
                <div className="employee-form__stepper">
                  <Stepper
                    isCreateForm={isCreateForm}
                    employeeName={getFullName({
                      firstName: initialFormData.firstName || '',
                      middleName: initialFormData.middleName || '',
                      lastName: initialFormData.lastName || '',
                    })}
                    stepperNavigation={stepperNavigationItems}
                    onNavigate={(id: number) => {
                      handleNavigate(id);
                    }}
                    currentForm={currentForm}
                  />
                </div>

                {isCreateForm && (
                  <div className="employee-form__import">
                    <ImportSheet setInitialValues={setInitialValues} />
                  </div>
                )}
              </div>

              <div className="employee-form__form-wrapper">
                <form onSubmit={handleSubmit} autoComplete="off">
                  <div className="form-wrapper">
                    <FormWrapperHeader
                      currentForm={currentForm.id}
                      sizeOfStepper={stepperNavigationItems.length}
                      label={currentForm.label}
                    />
                    <ActiveComponent
                      {...formikProps}
                      shouldForcefullyShowError={shouldForcefullyShowError}
                      duplicateErrors={duplicateErrors}
                      setDuplicateErrors={setDuplicateErrors}
                      leaveIssuerDebounceOptions={
                        ((currentForm.id === 1 && leaveIssuerDebounceOptions) || leaveIssuerDebounceOptions) as [
                          boolean,
                          Array<{ value: string | number; label?: string }>,
                          string,
                          (code: string) => void,
                        ]
                      }
                      teamManagerDebounceOptions={
                        ((currentForm.id === 1 && teamManagerDebounceOptions) || teamManagerDebounceOptions) as [
                          boolean,
                          Array<{ value: string | number; label?: string }>,
                          string,
                          (code: string) => void,
                        ]
                      }
                      appraiserDebounceOptions={
                        ((currentForm.id === 4 && appraiserDebounceOptions) || appraiserDebounceOptions) as [
                          boolean,
                          Array<{ value: string | number; label?: string }>,
                          string,
                          (code: string) => void,
                        ]
                      }
                      coAppraiserDebounceOptions={
                        ((currentForm.id === 4 && coAppraiserDebounceOptions) || coAppraiserDebounceOptions) as [
                          boolean,
                          Array<{ value: string | number; label?: string }>,
                          string,
                          (code: string) => void,
                        ]
                      }
                      isCreateForm={isCreateForm}
                      canEdit={canEdit}
                      currentForm={currentForm}
                      setCurrentFormState={(state: string) => updateCurrentFormState(currentForm.id, state)}
                      sizeOfStepper={stepperNavigationItems.length}
                      onNavigate={handleNavigate}
                      departments={currentForm.id === 1 && departments}
                      employees={employees}
                      roles={currentForm.id === 1 && roles}
                      openDesignationModal={(type: string, editIndex?: number) =>
                        setDesignationModal({ isOpen: true, type, editIndex })
                      }
                      openEmployeeStatusModal={(type: string, editIndex?: number) =>
                        setEmployeeStatusModal({ isOpen: true, type, editIndex })
                      }
                    />
                    <FormWrapperBottom
                      isModalShown={isUpComingStatusTerminated(
                        (formikProps.values.empStatusHistory || []).map((item) => ({
                          transitionDate: String(item.transitionDate || ''),
                          engagementStatus: { name: item.engagementStatus?.name || '' },
                        }))
                      )}
                      alertMessage={{
                        text:
                          `All the leave credits of ${formikProps.values.firstName} ${formikProps.values.lastName} ` +
                          "will be cleared and can't be reverted back if status is updated to terminated. " +
                          'Do you want to continue?',
                      }}
                      onSubmit={formikProps.handleSubmit}
                      onNavigate={handleNavigate}
                      currentForm={currentForm.id}
                      sizeOfStepper={stepperNavigationItems.length}
                      isValid={checkIsValid()}
                      isLoading={isSubmitting || formikProps.isValidating || isCheckingDuplicates}
                    />
                  </div>
                </form>
              </div>
            </div>
          </>
        );
      }}
    </Formik>
  );
};

export default FormWrapper;
